import { prefersReducedMotion } from '../lib/gsap';

const SW = typeof window !== 'undefined' && window.innerWidth < 1024 ? 1.8 : 1;

const GOLD = '#C8AA73';
const PALE = '#E8D6AF';

const drawStyle = (active, delay = 0, dur = 1.6) => ({
  strokeDasharray: 1,
  strokeDashoffset: active ? 0 : 1,
  transition: `stroke-dashoffset ${dur}s cubic-bezier(0.22,1,0.36,1) ${delay}s`,
});

const Defs = () => (
  <defs>
    <linearGradient id="sv-metal" x1="0" y1="0" x2="1" y2="1">
      <stop offset="0" stopColor="#24221e" />
      <stop offset="0.5" stopColor="#0e0e0e" />
      <stop offset="1" stopColor="#1b1916" />
    </linearGradient>
    <linearGradient id="sv-edge" x1="0" y1="0" x2="1" y2="1">
      <stop offset="0" stopColor={PALE} stopOpacity="0.9" />
      <stop offset="0.45" stopColor={GOLD} stopOpacity="0.15" />
      <stop offset="1" stopColor={GOLD} stopOpacity="0.6" />
    </linearGradient>
    <radialGradient id="sv-glow" cx="0.5" cy="0.5" r="0.5">
      <stop offset="0" stopColor={GOLD} stopOpacity="0.16" />
      <stop offset="1" stopColor={GOLD} stopOpacity="0" />
    </radialGradient>
  </defs>
);

const providerNodes = Array.from({ length: 15 }, (_, i) => {
  const a = (-70 + i * 10) * (Math.PI / 180);
  return { x: 330 - Math.cos(a) * 270, y: 300 + Math.sin(a) * 240 };
});

export const AggregatorVisual = ({ active = true }) => (
  <svg viewBox="0 0 600 600" className="w-full h-full" fill="none" data-testid="visual-aggregator">
    <Defs />
    <circle cx="360" cy="300" r="200" fill="url(#sv-glow)" />
    {providerNodes.map((n, i) => (
      <path
        key={`l${i}`}
        d={`M${n.x} ${n.y} C ${n.x + 120} ${n.y}, ${300} ${300 + (n.y - 300) * 0.2}, 340 300`}
        stroke={i % 4 === 0 ? GOLD : 'rgba(244,240,232,0.16)'}
        strokeWidth={(i % 4 === 0 ? 0.9 : 0.6) * SW}
        pathLength="1"
        style={drawStyle(active, 0.1 + i * 0.05)}
      />
    ))}
    {providerNodes.map((n, i) => (
      <g key={`n${i}`} style={{ opacity: active ? 1 : 0, transition: `opacity .8s ${0.05 * i}s` }}>
        <circle cx={n.x} cy={n.y} r={i % 4 === 0 ? 4 : 2.6} fill={i % 4 === 0 ? GOLD : '#3a3731'} />
        {i % 4 === 0 && <circle cx={n.x} cy={n.y} r="10" stroke={GOLD} strokeOpacity="0.35" strokeWidth={0.6 * SW} />}
      </g>
    ))}
    <g transform="translate(360 300)">
      <rect x="-46" y="-46" width="92" height="92" transform="rotate(45)" fill="url(#sv-metal)" stroke="url(#sv-edge)" strokeWidth={1 * SW} />
      <rect x="-26" y="-26" width="52" height="52" transform="rotate(45)" stroke={GOLD} strokeOpacity="0.5" strokeWidth={0.7 * SW} className="animate-spin-slow" style={{ transformBox: 'fill-box', transformOrigin: 'center' }} />
      <circle r="4" fill={PALE} />
    </g>
    <path d="M425 300 H 590" stroke={GOLD} strokeWidth={1.1 * SW} pathLength="1" style={drawStyle(active, 1.1, 1.2)} />
    <path d="M425 300 H 590" stroke={PALE} strokeWidth={1.1 * SW} className="animate-dash" opacity={active ? 0.8 : 0} />
  </svg>
);

const TILES = [
  [0, 0, 2, 1], [2, 0, 1, 1], [0, 1, 1, 2], [1, 1, 1, 1], [2, 1, 1, 2], [1, 2, 1, 1],
];

export const PlatformVisual = ({ active = true }) => {
  const u = 132;
  const g = 12;
  return (
    <svg viewBox="0 0 600 600" className="w-full h-full" fill="none" data-testid="visual-platform">
      <Defs />
      <circle cx="300" cy="300" r="240" fill="url(#sv-glow)" />
      <g transform="translate(96 96)">
        {TILES.map(([x, y, w, h], i) => {
          const off = [[-60, -40], [70, -60], [-80, 30], [0, 0], [80, 50], [20, 90]][i];
          return (
            <g
              key={i}
              style={{
                transform: active ? 'translate(0,0)' : `translate(${off[0]}px, ${off[1]}px)`,
                opacity: active ? 1 : 0.25,
                transition: `transform 1.4s cubic-bezier(0.22,1,0.36,1) ${i * 0.08}s, opacity 1s ${i * 0.08}s`,
              }}
            >
              <rect
                x={x * (u + g)}
                y={y * (u + g)}
                width={w * u + (w - 1) * g}
                height={h * u + (h - 1) * g}
                rx="10"
                fill={i === 3 ? 'rgba(200,170,115,0.08)' : 'url(#sv-metal)'}
                stroke={i === 3 ? GOLD : 'url(#sv-edge)'}
                strokeWidth={(i === 3 ? 1.1 : 0.8) * SW}
              />
              <line
                x1={x * (u + g) + 18}
                y1={y * (u + g) + 22}
                x2={x * (u + g) + 18 + (i === 0 ? 90 : 40)}
                y2={y * (u + g) + 22}
                stroke={i === 3 ? PALE : 'rgba(244,240,232,0.22)'}
                strokeWidth={1 * SW}
              />
              <circle cx={x * (u + g) + w * u + (w - 1) * g - 20} cy={y * (u + g) + 22} r="2.5" fill={i % 2 ? GOLD : '#4a463e'} className={i === 3 ? 'animate-pulse-soft' : ''} />
            </g>
          );
        })}
      </g>
      <rect x="80" y="80" width="440" height="440" rx="18" stroke="rgba(200,170,115,0.25)" strokeWidth={0.6 * SW} pathLength="1" style={drawStyle(active, 0.6, 2)} />
    </svg>
  );
};

const IN_LANES = [-150, -100, -50, 0, 50, 100, 150];
const OUT_LANES = [-90, 0, 90];

export const PaymentsVisual = ({ active = true }) => {
  const motion = !prefersReducedMotion();
  return (
    <svg viewBox="0 0 600 600" className="w-full h-full" fill="none" data-testid="visual-payments">
      <Defs />
      <circle cx="300" cy="300" r="230" fill="url(#sv-glow)" />
      {IN_LANES.map((o, i) => (
        <path key={`i${i}`} id={`pin${i}`} d={`M20 ${300 + o} C 160 ${300 + o}, 200 ${300 + o * 0.12}, 290 ${300 + o * 0.12}`} stroke="rgba(244,240,232,0.14)" strokeWidth={0.7 * SW} pathLength="1" style={drawStyle(active, i * 0.06)} />
      ))}
      {OUT_LANES.map((o, i) => (
        <path key={`o${i}`} id={`pout${i}`} d={`M310 ${300 + o * 0.15} C 400 ${300 + o * 0.15}, 440 ${300 + o}, 580 ${300 + o}`} stroke={GOLD} strokeOpacity="0.55" strokeWidth={0.9 * SW} pathLength="1" style={drawStyle(active, 0.8 + i * 0.1)} />
      ))}
      <rect x="282" y="250" width="36" height="100" rx="18" fill="url(#sv-metal)" stroke="url(#sv-edge)" strokeWidth={1 * SW} />
      <line x1="300" y1="268" x2="300" y2="332" stroke={GOLD} strokeWidth={1 * SW} className="animate-pulse-soft" />
      {motion && active && IN_LANES.map((_, i) => (
        <circle key={`d${i}`} r="2.4" fill={i % 3 === 0 ? PALE : '#8a8478'}>
          <animateMotion dur={`${3.2 + (i % 3) * 0.7}s`} repeatCount="indefinite" begin={`${i * 0.4}s`}>
            <mpath href={`#pin${i}`} />
          </animateMotion>
        </circle>
      ))}
      {motion && active && OUT_LANES.map((_, i) => (
        <circle key={`e${i}`} r="3" fill={PALE}>
          <animateMotion dur="2.8s" repeatCount="indefinite" begin={`${0.6 + i * 0.5}s`}>
            <mpath href={`#pout${i}`} />
          </animateMotion>
        </circle>
      ))}
    </svg>
  );
};

const hex = (cx, cy, r) =>
  Array.from({ length: 6 }, (_, i) => {
    const a = (Math.PI / 3) * i + Math.PI / 6;
    return `${cx + r * Math.cos(a)},${cy + r * Math.sin(a)}`;
  }).join(' ');

const BLOCKS = [
  [130, 430], [215, 355], [300, 280], [385, 205], [470, 130],
];

export const CryptoVisual = ({ active = true }) => (
  <svg viewBox="0 0 600 600" className="w-full h-full" fill="none" data-testid="visual-crypto">
    <Defs />
    <circle cx="300" cy="280" r="230" fill="url(#sv-glow)" />
    <path d={`M${BLOCKS.map((b) => b.join(' ')).join(' L')}`} stroke={GOLD} strokeOpacity="0.6" strokeWidth={0.9 * SW} pathLength="1" style={drawStyle(active, 0.2, 2)} />
    {BLOCKS.map(([x, y], i) => (
      <g
        key={i}
        style={{
          transform: active ? 'translateY(0)' : 'translateY(24px)',
          opacity: active ? 1 : 0,
          transition: `transform 1.2s cubic-bezier(0.22,1,0.36,1) ${0.15 * i}s, opacity 1s ${0.15 * i}s`,
        }}
      >
        <polygon points={hex(x, y, 48)} fill="url(#sv-metal)" stroke="url(#sv-edge)" strokeWidth={1 * SW} />
        <polygon points={hex(x, y, 28)} stroke={GOLD} strokeWidth={0.8 * SW} className="animate-pulse-soft" style={{ animationDelay: `${i * 0.7}s` }} />
        <circle cx={x} cy={y} r="2.5" fill={i === 4 ? PALE : '#6a655b'} />
      </g>
    ))}
    {[...Array(9)].map((_, i) => (
      <line key={i} x1={60 + i * 60} y1="540" x2={60 + i * 60} y2="552" stroke="rgba(244,240,232,0.2)" strokeWidth={0.8 * SW} />
    ))}
    <line x1="60" y1="546" x2="540" y2="546" stroke="rgba(244,240,232,0.12)" strokeWidth={0.6 * SW} />
  </svg>
);

export const SERVICE_VISUALS = {
  aggregator: AggregatorVisual,
  platform: PlatformVisual,
  payments: PaymentsVisual,
  crypto: CryptoVisual,
};
